import _isEqual from 'lodash/isEqual';
import useService from '../../../../hooks/useService';
import FhirSearchService from '../../../../services/FhirSearchService';
import { useSelector } from 'react-redux';
import { projectPickerSelectors } from '../../../../redux/modules/projectPicker';
import { AxiosResponse } from 'axios';
import { useCustomCompareEffect } from 'react-use';
import { useActiveProjectCohortId } from '../../../../hooks';

const fhirSearchService = new FhirSearchService();

export type Hits<T> = {
  hits: {
    hits: {
      _source: T;
    }[];
  };
};

type Options = {
  /**
   * This hook searches resources using the active cohortId by default. To disable
   * this behavior, set this option to `true` to search all resources not just
   * those associated with the cohort or the patients in that cohort.
   */
  searchEntireProject?: boolean;
};

function useFhirSearch<TResult>(
  query: any,
  opts: Options = {},
): [TResult, boolean] {
  const activeProjectId = useSelector(
    projectPickerSelectors.selectActiveProjectId,
  );
  const [search, searchState, resetSearchState] = useService(
    fhirSearchService.search,
  );
  const activeCohortId = useActiveProjectCohortId();
  const cohortToUse = opts.searchEntireProject ? undefined : activeCohortId;

  useCustomCompareEffect(
    () => {
      if (!query || !activeProjectId) return;
      resetSearchState();
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      search(activeProjectId, query, null, {}, cohortToUse);
    },
    [activeProjectId, query, cohortToUse, resetSearchState, search],
    ([prevProject, prevQuery, prevCohortId], [project, query, cohortId]) =>
      _isEqual(prevProject, project) &&
      _isEqual(prevQuery, query) &&
      prevCohortId === cohortId,
  );

  const data = (searchState.response as AxiosResponse<TResult>)?.data || null;
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return [data, searchState.isFetching || !searchState.lastFetchAttempt];
}

export default useFhirSearch;
